import { createMemo, createResource, For, Show } from "solid-js"
import { getWorkspaceSettings } from "@/cowork/api"
import { useLanguage } from "@/context/language"

type Quota = { label: string; used: number; limit: number; displayUsed?: number; displayLimit?: number }

export function CoworkUsagePage() {
  const language = useLanguage()
  const [settings] = createResource(getWorkspaceSettings)
  const quotas = createMemo(() => {
    const workspace = settings()
    if (!workspace) return []
    const items: Quota[] = []
    if (workspace.seat_limit) {
      items.push({
        label: language.t("cowork.enterprise.settings.seats"),
        used: workspace.seat_used ?? 0,
        limit: workspace.seat_limit,
      })
    }
    const trial = workspace.trial
    if (trial) {
      items.push(
        { label: language.t("cowork.usage.companies"), used: trial.companies_used, limit: trial.companies_limit },
        { label: language.t("cowork.usage.projects"), used: trial.projects_used, limit: trial.projects_limit },
        { label: language.t("cowork.usage.reports"), used: trial.reports_used, limit: trial.reports_limit },
        {
          label: language.t("cowork.usage.storage"),
          used: trial.storage_used_bytes,
          limit: trial.storage_limit_bytes,
          displayUsed: Math.ceil(trial.storage_used_bytes / 1024 / 1024),
          displayLimit: trial.storage_limit_bytes / 1024 / 1024,
        },
        { label: language.t("cowork.usage.chats"), used: trial.chats_used, limit: trial.chats_limit },
        { label: language.t("cowork.usage.agentRequests"), used: trial.messages_used, limit: trial.messages_limit },
      )
    }
    return items
  })

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1100px]">
        <header>
          <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.usage.title")}</h1>
          <p class="mt-1 text-[13px] text-v2-text-text-muted">{language.t("cowork.usage.workspaceDescription")}</p>
        </header>

        <Show when={!settings.loading} fallback={<div class="mt-7 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-6 text-v2-text-text-muted">{language.t("common.loading")}</div>}>
          <Show when={!settings.error} fallback={<p role="alert" class="mt-7 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-6 text-v2-state-fg-danger">{language.t("cowork.usage.loadError")}</p>}>
            <Show when={settings()}>
              {(workspace) => (
                <>
                  <section class="mt-7 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 px-5 py-5 sm:px-6">
                    <div class="flex flex-wrap items-center justify-between gap-x-8 gap-y-4">
                      <div>
                        <p class="text-[11px] font-medium uppercase tracking-[0.12em] text-v2-text-text-muted">{language.t("cowork.enterprise.settings.workspace")}</p>
                        <h2 class="mt-1 text-[22px] font-semibold tracking-[-0.02em] text-v2-text-text-base">{workspace().workspace_name}</h2>
                      </div>
                      <Show when={workspace().trial}>
                        {(trial) => (
                          <div class="rounded-lg bg-v2-background-bg-layer-02 px-4 py-3">
                            <p class="text-[12px] font-semibold text-v2-text-text-base">{language.t("cowork.enterprise.settings.trial")}</p>
                            <Show when={trial().ends_at}>
                              {(endsAt) => (
                                <p class="mt-1 text-[12px] text-v2-text-text-muted">
                                  {language.t(new Date(endsAt()).getTime() <= Date.now() ? "cowork.trial.expired" : "cowork.trial.ends", { date: new Date(endsAt()).toLocaleDateString() })}
                                </p>
                              )}
                            </Show>
                          </div>
                        )}
                      </Show>
                    </div>
                  </section>

                  <section class="mt-8" aria-labelledby="cowork-usage-allowances">
                    <h2 id="cowork-usage-allowances" class="settings-v2-section-title">{language.t("cowork.usage.allowances")}</h2>
                    <div class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <For each={quotas()}>
                        {(quota) => <QuotaCard {...quota} valueLabel={language.t("cowork.usage.quota", {
                          used: String(quota.displayUsed ?? quota.used),
                          limit: String(quota.displayLimit ?? quota.limit),
                        })} />}
                      </For>
                    </div>
                  </section>
                </>
              )}
            </Show>
          </Show>
        </Show>
      </div>
    </div>
  )
}

function QuotaCard(props: Quota & { valueLabel: string }) {
  const percent = () => Math.min(100, Math.max(0, (props.used / Math.max(1, props.limit)) * 100))

  return (
    <div class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-5">
      <p class="text-[12px] font-medium text-v2-text-text-muted">{props.label}</p>
      <p class="mt-4 text-[25px] font-semibold leading-none tracking-[-0.02em] text-v2-text-text-base">
        {props.displayUsed ?? props.used}
        <span class="ml-1 text-[14px] font-normal text-v2-text-text-muted">/ {props.displayLimit ?? props.limit}</span>
      </p>
      <div
        class="mt-5 h-1.5 overflow-hidden rounded-full bg-v2-background-bg-layer-02"
        role="progressbar"
        aria-label={props.label}
        aria-valuemin={0}
        aria-valuemax={props.limit}
        aria-valuenow={Math.min(props.used, props.limit)}
        aria-valuetext={props.valueLabel}
      >
        <div class="h-full rounded-full bg-v2-background-bg-contrast" style={{ width: `${percent()}%` }} />
      </div>
      <p class="mt-2 text-[11px] text-v2-text-text-muted">{props.valueLabel}</p>
    </div>
  )
}
